import type { ReviewTrendDataPoint } from "@/lib/organisms/review-trend-chart";
import type { AirbnbReview } from "@/models/responses";
import { formatDateToISODate } from "@/utils/dates";

// The seven plotted dimensions. `averageRating` is always present; the six
// category columns are nullable (NUMERIC(2,1), null across the pre-scraper
// backfill) and are excluded per-apartment when null.
const METRIC_KEYS = ["averageRating", "cleanliness", "accuracy", "communication", "location", "checkIn", "value"] as const;

type MetricKey = (typeof METRIC_KEYS)[number];

// airbnb-review-performance-table/FR-022: aggregate the portfolio per date as a
// volume-weighted mean over `numberOfReviews` — Σ(rating·n)/Σ(n) — rather than an
// unweighted mean-of-means, so the chart agrees with the volume-correct table.
// A metric that is null on an apartment is excluded from BOTH the weighted sum
// and the weight; a metric null on every apartment for a date yields `undefined`
// (not 0), so the chart shows a gap rather than a false zero.
export function computeWeightedPortfolioTrend(reviews: AirbnbReview[]): ReviewTrendDataPoint[] {
    const byDate = new Map<string, AirbnbReview[]>();
    for (const review of reviews) {
        const dateKey = formatDateToISODate(review.date);
        const bucket = byDate.get(dateKey);
        if (bucket) {
            bucket.push(review);
        } else {
            byDate.set(dateKey, [review]);
        }
    }

    return Array.from(byDate.entries())
        .map(([date, dateReviews]) => {
            const point: ReviewTrendDataPoint = { date };
            for (const metric of METRIC_KEYS) {
                point[metric] = weightedMean(dateReviews, metric);
            }
            return point;
        })
        .sort((a, b) => a.date.localeCompare(b.date));
}

function weightedMean(reviews: AirbnbReview[], metric: MetricKey): number | undefined {
    let weightedSum = 0;
    let weight = 0;
    for (const review of reviews) {
        const value = review[metric];
        if (value === null || value === undefined) {
            continue;
        }
        weightedSum += value * review.numberOfReviews;
        weight += review.numberOfReviews;
    }
    return weight > 0 ? weightedSum / weight : undefined;
}
